import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ChevronDown, Languages } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "./ui/dropdown-menu";
import { SUPPORTED_LANGS, LANG_STORAGE_KEY } from "../i18n";

/**
 * FutureTec language switcher (Phase 1.1).
 * Sibling to CountrySwitcher — language and country are independent
 * axes. Persists to LocalStorage `futuretec_lang` and emits a
 * `futuretec:lang-changed` CustomEvent for downstream listeners.
 * Toast on change is emitted in the newly-selected language.
 */
export default function LanguageSwitcher() {
  const { t, i18n } = useTranslation("common");
  const [code, setCode] = React.useState(() => i18n.language || "en");

  // Keep local state in sync when i18next changes externally
  // (e.g. detector picking up a stored value on first mount).
  React.useEffect(() => {
    const onChanged = (lng) => setCode(lng);
    i18n.on("languageChanged", onChanged);
    return () => i18n.off("languageChanged", onChanged);
  }, [i18n]);

  const current =
    SUPPORTED_LANGS.find((l) => l.code === code) ||
    SUPPORTED_LANGS.find((l) => code.startsWith(l.code)) ||
    SUPPORTED_LANGS[0];

  const pick = async (next) => {
    // Fire on every click (no `next === code` early-return). See
    // CountrySwitcher for the rationale: silent no-ops made stale toasts
    // look like they belonged to the latest click.
    await i18n.changeLanguage(next);
    setCode(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
      window.dispatchEvent(
        new CustomEvent("futuretec:lang-changed", { detail: { code: next } })
      );
    } catch (_) {
      /* ignore */
    }
    const picked = SUPPORTED_LANGS.find((l) => l.code === next);
    // Use i18n.t() (module-scoped) rather than the hook-captured `t` so the
    // toast prefix is resolved in the NEW language rather than the previous
    // render's snapshot.
    if (picked) toast.success(`${i18n.t("language.toastPrefix")}: ${picked.native}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="ftg-btn ftg-btn--ghost ftg-language-switcher"
          aria-label={`${t("language.switcherLabel")}: ${current.native}`}
          data-testid="language-switcher-trigger"
        >
          <Languages size={14} aria-hidden="true" />
          <span className="ftg-language-switcher__native">{current.native}</span>
          <ChevronDown size={14} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="language-switcher-menu">
        <DropdownMenuLabel>{t("language.chooseLanguage")}</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuRadioGroup value={code}>
          {SUPPORTED_LANGS.map((l) => (
            <DropdownMenuRadioItem
              key={l.code}
              value={l.code}
              onSelect={() => pick(l.code)}
              data-testid={`language-switcher-option-${l.code}`}
            >
              {l.native}
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
